import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { useOrgId } from '@/lib/org';
import { useTagGroups, useTrackingMutation, type Tag, type TagGroup } from '@/lib/tracking';

export const Route = createFileRoute('/settings/tags')({ component: Tags });

type Editing =
  | { kind: 'tag'; tag: Tag | null; groupId: string | null }
  | { kind: 'group'; group: TagGroup | null }
  | null;

/** Screen L3 (document 06): tags in groups. Tags never touch the ledger. */
function Tags() {
  const orgId = useOrgId();
  const me = useMe();
  const isStaff = me.data?.isStaff ?? false;
  const list = useTagGroups('all');
  const [editing, setEditing] = useState<Editing>(null);
  const act = useTrackingMutation(
    (o, v: { path: string; method: 'PATCH' | 'DELETE'; body?: unknown }) =>
      api<unknown>(`/orgs/${o}/${v.path}`, {
        method: v.method,
        ...(v.body ? { body: JSON.stringify(v.body) } : {}),
      }),
  );
  if (!orgId) return <EmptyState title="Select a client" />;

  const rows = (tags: Tag[]) =>
    tags.map((t) => (
      <TableRow key={t.id} data-testid={`tag-${t.name}`}>
        <TableCell className="pl-8">{t.name}</TableCell>
        <TableCell className="tnum text-right">{t.uses}</TableCell>
        <TableCell>{t.active ? 'Active' : 'Inactive'}</TableCell>
        <TableCell>
          {isStaff ? (
            <div className="flex items-center">
              <Button
                variant="link"
                size="sm"
                className="px-1"
                onClick={() => setEditing({ kind: 'tag', tag: t, groupId: t.groupId })}
              >
                Edit
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon-sm" aria-label={`More actions for ${t.name}`}>
                    <ChevronDown className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() =>
                      act.mutate({
                        path: `tags/${t.id}`,
                        method: 'PATCH',
                        body: { active: !t.active },
                      })
                    }
                  >
                    {t.active ? 'Make inactive' : 'Make active'}
                  </DropdownMenuItem>
                  {t.uses === 0 ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        onSelect={() =>
                          act.mutate(
                            { path: `tags/${t.id}`, method: 'DELETE' },
                            { onSuccess: () => toast(`Tag "${t.name}" deleted.`) },
                          )
                        }
                      >
                        Delete
                      </DropdownMenuItem>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : null}
        </TableCell>
      </TableRow>
    ));

  return (
    <div>
      <PageHeader
        title="Tags"
        description="Label transactions for your own tracking, such as a campaign or an event. Tags can be changed on posted transactions; they never change the books."
        actions={
          isStaff ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="tags-new">
                  New <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => setEditing({ kind: 'tag', tag: null, groupId: null })}
                >
                  Tag
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setEditing({ kind: 'group', group: null })}>
                  Tag group
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null
        }
      />
      <ErrorBanner>{act.error?.message ?? null}</ErrorBanner>
      {list.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : list.isError ? (
        <EmptyState title="Could not load tags" hint={list.error.message} />
      ) : !list.data.groups.length && !list.data.ungrouped.length ? (
        <EmptyState
          title="No tags yet"
          hint="For example a group Campaign with Ramadan and Diwali."
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-[140px] text-right">Transactions</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead className="w-[160px]">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data.groups.map((g) => (
                <GroupRows
                  key={g.id}
                  group={g}
                  isStaff={isStaff}
                  onEdit={() => setEditing({ kind: 'group', group: g })}
                  onAddTag={() => setEditing({ kind: 'tag', tag: null, groupId: g.id })}
                  onDelete={() =>
                    act.mutate(
                      { path: `tag-groups/${g.id}`, method: 'DELETE' },
                      { onSuccess: () => toast(`Tag group "${g.name}" deleted.`) },
                    )
                  }
                >
                  {rows(g.tags)}
                </GroupRows>
              ))}
              {list.data.ungrouped.length ? (
                <>
                  <TableRow className="bg-muted/30">
                    <TableCell colSpan={4} className="font-medium">
                      Ungrouped tags
                    </TableCell>
                  </TableRow>
                  {rows(list.data.ungrouped)}
                </>
              ) : null}
            </TableBody>
          </Table>
        </div>
      )}
      <TagDialog
        editing={editing}
        groups={list.data?.groups ?? []}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}

function GroupRows({
  group,
  isStaff,
  onEdit,
  onAddTag,
  onDelete,
  children,
}: {
  group: TagGroup;
  isStaff: boolean;
  onEdit: () => void;
  onAddTag: () => void;
  onDelete: () => void;
  children: React.ReactNode;
}) {
  return (
    <>
      <TableRow className="bg-muted/30" data-testid={`tag-group-${group.name}`}>
        <TableCell colSpan={3} className="font-medium">
          {group.name}
        </TableCell>
        <TableCell>
          {isStaff ? (
            <div className="flex items-center">
              <Button variant="link" size="sm" className="px-1" onClick={onAddTag}>
                Add tag
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`More actions for ${group.name}`}
                  >
                    <ChevronDown className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={onEdit}>Rename</DropdownMenuItem>
                  {!group.tags.length ? (
                    <DropdownMenuItem variant="destructive" onSelect={onDelete}>
                      Delete
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : null}
        </TableCell>
      </TableRow>
      {children}
    </>
  );
}

function TagDialog({
  editing,
  groups,
  onClose,
}: {
  editing: Editing;
  groups: TagGroup[];
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [seeded, setSeeded] = useState<Editing>(null);
  if (editing !== seeded) {
    setSeeded(editing);
    setName(
      editing?.kind === 'tag'
        ? (editing.tag?.name ?? '')
        : editing?.kind === 'group'
          ? (editing.group?.name ?? '')
          : '',
    );
    setGroupId(editing?.kind === 'tag' ? (editing.groupId ?? '') : '');
  }
  const save = useTrackingMutation((orgId, e: NonNullable<Editing>) => {
    if (e.kind === 'group')
      return api<unknown>(`/orgs/${orgId}/tag-groups${e.group ? `/${e.group.id}` : ''}`, {
        method: e.group ? 'PATCH' : 'POST',
        body: JSON.stringify({ name: name.trim() }),
      });
    return api<unknown>(`/orgs/${orgId}/tags${e.tag ? `/${e.tag.id}` : ''}`, {
      method: e.tag ? 'PATCH' : 'POST',
      body: JSON.stringify({ name: name.trim(), groupId: groupId || null }),
    });
  });
  const isGroup = editing?.kind === 'group';
  const isNew = editing?.kind === 'group' ? !editing.group : !editing?.tag;
  return (
    <Dialog
      open={editing !== null}
      onOpenChange={(o) => {
        if (!o) {
          save.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isNew ? 'New' : 'Edit'} {isGroup ? 'tag group' : 'tag'}
          </DialogTitle>
          <DialogDescription>
            {isGroup ? 'Group names are unique.' : 'Tag names are unique within their group.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <ErrorBanner>{save.error?.message ?? null}</ErrorBanner>
          <Field label="Name *">
            {(id) => (
              <Input
                id={id}
                value={name}
                maxLength={40}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
          {!isGroup ? (
            <Field label="Group">
              {(id) => (
                <NativeSelect id={id} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                  <option value="">No group</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || !name.trim()}
            onClick={() =>
              editing &&
              save.mutate(editing, {
                onSuccess: () => {
                  toast(`${isGroup ? 'Tag group' : 'Tag'} "${name.trim()}" saved.`);
                  onClose();
                },
              })
            }
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
